import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTheme } from '@/hooks/useTheme';
import type { ThemeMode } from '@/lib/theme';

import { formatDay, formatUsd } from './slack-bot-view';

import type { DashboardDay } from './slack-bot.types';
import type { ChartOptions, ScriptableContext } from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// The series in stacking order. Colors are the dataviz skill's categorical
// slots, checked with its validator in both modes; identity stays with the
// source if a window has none of one
const SERIES = [
  {
    key: 'people_micro_usd',
    label: 'People asking',
    light: '#2a78d6',
    dark: '#3987e5',
  },
  {
    key: 'scheduled_micro_usd',
    label: 'Scheduled jobs',
    light: '#eb6834',
    dark: '#d95926',
  },
  {
    key: 'untagged_micro_usd',
    label: 'Untagged',
    light: '#1baf7a',
    dark: '#199e70',
  },
  {
    key: 'background_micro_usd',
    label: 'Judge and summaries',
    light: '#eda100',
    dark: '#c98500',
  },
] as const;

const TOKENS: Record<
  ThemeMode,
  { surface: string; grid: string; tick: string; text: string }
> = {
  light: {
    surface: '#ffffff',
    grid: 'rgba(0, 0, 0, 0.07)',
    tick: '#6b7280',
    text: '#374151',
  },
  dark: {
    surface: '#020817',
    grid: 'rgba(255, 255, 255, 0.07)',
    tick: '#9ca3af',
    text: '#e5e7eb',
  },
};

const MICRO = 1_000_000;

const totalOf = (day: DashboardDay): number =>
  SERIES.reduce((sum, series) => sum + day[series.key], 0);

export function DailySpendChart({ days }: { days: DashboardDay[] }) {
  const theme = useTheme();
  const tokens = TOKENS[theme];

  const data = useMemo(
    () => ({
      labels: days.map((day) => formatDay(day.day)),
      datasets: SERIES.map((series, seriesIndex) => ({
        label: series.label,
        data: days.map((day) => day[series.key] / MICRO),
        backgroundColor: series[theme],
        borderColor: tokens.surface,
        borderWidth: { top: 2 },
        borderSkipped: false as const,
        // Only the top of a day's stack is rounded; the baseline stays square
        borderRadius: (context: ScriptableContext<'bar'>) => {
          const day = days[context.dataIndex];
          if (day === undefined) return 0;
          const isTop = SERIES.slice(seriesIndex + 1).every(
            (later) => day[later.key] === 0
          );
          return isTop
            ? { topLeft: 4, topRight: 4, bottomLeft: 0, bottomRight: 0 }
            : 0;
        },
        maxBarThickness: 24,
        stack: 'spend',
      })),
    }),
    [days, theme, tokens.surface]
  );

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          position: 'top',
          align: 'start',
          labels: {
            color: tokens.text,
            boxWidth: 10,
            boxHeight: 10,
            useBorderRadius: true,
            borderRadius: 2,
            font: { size: 12 },
          },
        },
        tooltip: {
          filter: (item) => item.parsed.y !== 0,
          callbacks: {
            label: (item) =>
              `${item.dataset.label ?? ''}: ${formatUsd((item.parsed.y ?? 0) * MICRO)}`,
            footer: (items) => {
              const day = days[items[0]?.dataIndex ?? -1];
              return day === undefined
                ? ''
                : `Total: ${formatUsd(totalOf(day))}`;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          border: { color: tokens.grid },
          ticks: {
            color: tokens.tick,
            autoSkip: true,
            maxRotation: 0,
            font: { size: 11 },
          },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: tokens.grid },
          border: { display: false },
          ticks: {
            color: tokens.tick,
            font: { size: 11 },
            callback: (value) => `$${Number(value).toLocaleString('en-US')}`,
          },
        },
      },
    }),
    [days, tokens]
  );

  return (
    <div className="space-y-3">
      <div
        className="relative h-64 min-w-0 overflow-hidden"
        role="img"
        aria-label="Daily spend, stacked by what it went on. The table below holds the same figures."
      >
        <Bar data={data} options={options} />
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-muted-foreground">
          Show as a table
        </summary>
        <Table className="mt-2">
          <TableHeader>
            <TableRow>
              <TableHead>Day</TableHead>
              {SERIES.map((series) => (
                <TableHead key={series.key} className="text-right">
                  {series.label}
                </TableHead>
              ))}
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {days.map((day) => (
              <TableRow key={day.day}>
                <TableCell>{formatDay(day.day)}</TableCell>
                {SERIES.map((series) => (
                  <TableCell key={series.key} className="text-right">
                    {formatUsd(day[series.key])}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  {formatUsd(totalOf(day))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </details>
    </div>
  );
}
